import { Component, OnInit, Inject, signal, DestroyRef, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
  MatDialog,
} from '@angular/material/dialog';
import { User } from '../../../interfaces';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  ValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomValidators } from 'angular-custom-validators';
import { generateRandomString, openSnackbar, ucFirst, valueToDate } from 'src/app/globals';
import moment from 'moment';
import { dataStore, functions } from 'src/app/app.component';
import { db } from 'src/app/app.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption, MatSelectTrigger } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Role } from 'src/app/enums';
import { MatChipsModule, MatChipSet } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { AddressValidationFlowService } from 'src/app/services/address-validation-flow.service';
import {
  createEmployeePhotoDraft,
  getInitial,
  EmployeeProfileService,
} from 'src/app/services/employee-profile.service';
export interface DialogData {
  user: any;
}

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatIconButton,
    MatSuffix,
    MatTooltip,
    MatIcon,
    MatSelect,
    MatOption,
    MatSelectTrigger,
    MatChipSet,
    MatSlideToggle,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatChipsModule,
    CommonModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatProgressSpinner,
  ],
})
export class EditEmployeeDialogComponent implements OnInit {
  user = signal<User>(undefined);
  userForm: UntypedFormGroup;
  newUser = signal(true);
  userSelected = signal(false);
  saving = signal(false);
  isLookingUp = signal(false);
  addressNotFound = signal(false);
  private destroyRef = inject(DestroyRef);
  private readonly photoDraft = createEmployeePhotoDraft(this.destroyRef);
  private readonly addressService = inject(AddressValidationFlowService);
  private resolvedAddressData: Record<string, any> = {};
  private readonly profileService = inject(EmployeeProfileService);
  selectedPhoto = this.photoDraft.selectedPhoto;
  photoPreviewUrl = this.photoDraft.photoPreviewUrl;
  photoValidationError = this.photoDraft.photoValidationError;
  photoRemoved = this.photoDraft.photoRemoved;
  readonly getInitial = getInitial;

  organisationId = dataStore.selectedOrganisation.id;
  dataStore = dataStore;
  Role = Role;
  hasPersonalDataAccess =
    dataStore.paidFunctionalities?.usesEmployeePersonalDataManagement ?? false;
  canShowEmployeePhotos = dataStore.paidFunctionalities?.usesEmployeeListForStaff === true;
  readonly personalDataFields = [
    'postal',
    'houseNumber',
    'houseNumberAddition',
    'streetName',
    'city',
    'hiddenRemarks',
    'startDate',
    'vogDate',
    'driversTestDate',
  ];

  getRoleLabel(role: Role): string {
    const labels: Record<Role, string> = {
      [Role.Administrator]: 'Administrator',
      [Role.Planner]: 'Planner',
      [Role.RidePlanner]: 'Ritplanner',
      [Role.Driver]: 'Chauffeur',
      [Role.CoDriver]: 'Bijrijder',
      [Role.Other]: 'Overige',
    };
    return labels[role] || (role as string);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<EditEmployeeDialogComponent>,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.userForm = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
      name: ['', Validators.required],
      roles: [[], []],
      phone: ['', [Validators.required, CustomValidators.phone]],
      passenger: [],
    });

    if (this.hasPersonalDataAccess) {
      this.addressService.addControls(this.userForm);
      this.userForm.addControl('hiddenRemarks', this.fb.control(''));
      this.userForm.addControl(
        'startDate',
        this.fb.control(null, [Validators.required, this.validDate]),
      );
      this.userForm.addControl(
        'vogDate',
        this.fb.control(null, [Validators.required, this.dateNotInFuture]),
      );
      this.userForm.addControl(
        'driversTestDate',
        this.fb.control(null, [Validators.required, this.dateNotInFuture]),
      );
    }

    this.userForm.controls.email.valueChanges.subscribe(() => {
      if (this.userForm.controls.email.hasError('customError')) {
        this.userForm.controls.email.setErrors(null);
        this.userForm.controls.email.updateValueAndValidity();
      }
    });

    if (this.data.user) {
      console.log('data user', this.data.user);
      this.newUser.set(false);
      this.user.set(this.data.user);
      this.userSelected.set(true);
      this.photoDraft.reset(this.canShowEmployeePhotos ? (this.data.user.photoUrl ?? null) : null);

      const roles = [...(this.data.user.roles || [])];
      const userData = { ...this.data.user, roles };
      if (this.hasPersonalDataAccess) {
        userData.startDate = valueToDate(userData.startDate);
        userData.vogDate = valueToDate(userData.vogDate);
        userData.driversTestDate = valueToDate(userData.driversTestDate);
      }
      this.userForm.patchValue(userData);

      if (this.user().superuser) {
        this.userForm.controls.roles.disable();
      }
    } else {
      this.userForm.controls.email.setValidators(
        Validators.compose([Validators.required, CustomValidators.email]),
      );
      this.userForm.controls.roles.setValidators(Validators.required);
      this.userForm.registerControl('password', new UntypedFormControl('', Validators.required));
    }

    if (this.hasPersonalDataAccess) {
      this.setupAddressLookup();
    }

    if (!this.newUser()) {
      this.userForm.updateValueAndValidity({ emitEvent: false });
      this.userForm.markAllAsTouched();
    }
  }

  async save() {
    if (this.canSave()) {
      this.saving.set(true);
      this.resolvedAddressData = {};
      if (this.hasPersonalDataAccess && !(await this.confirmAddress())) {
        this.saving.set(false);
        return;
      }
      await this.setNewRights();
    }
  }

  onPhotoSelected(event: Event) {
    this.photoDraft.select(event);
  }

  removePhoto() {
    this.photoDraft.remove();
  }

  private async confirmAddress(): Promise<boolean> {
    const resolvedAddress = await this.addressService.resolveAddress(
      this.userForm.getRawValue(),
      this.user() ?? {},
      false,
      !this.newUser(),
    );
    if (!resolvedAddress) return false;

    this.resolvedAddressData = resolvedAddress;
    this.userForm.patchValue(resolvedAddress, { emitEvent: false });
    return true;
  }

  canSave(): boolean {
    if (
      !this.userForm?.valid ||
      this.saving() ||
      this.isLookingUp() ||
      this.photoValidationError()
    ) {
      return false;
    }
    if (!this.hasPersonalDataAccess) return true;

    const { streetName, city } = this.userForm.getRawValue();
    return String(streetName ?? '').trim().length > 0 && String(city ?? '').trim().length > 0;
  }

  async setNewRights() {
    const form = { ...this.userForm.getRawValue(), ...this.resolvedAddressData };
    const roles = form.roles || [];
    const withdrawRights = roles.length === 0 && !this.user()?.superuser;
    const callable = httpsCallable(functions, 'httpGrantRights');
    const params = {
      email: form.email,
      password: this.newUser() ? form.password : null,
      name: ucFirst(form.name),
      roles: roles,
      organisationId: this.organisationId,
      isEmployeeManagement: true,
      phone: form.phone,
      hidden: true,
      withdrawRights: withdrawRights,
      passenger: form.passenger,
      userId: this.newUser() ? null : (this.user()?.id ?? null),
      isNewUser: this.newUser(),
      ...(this.hasPersonalDataAccess
        ? {
            postal: form.postal?.replace(/\s/g, '').toUpperCase(),
            houseNumber: form.houseNumber,
            houseNumberAddition: form.houseNumberAddition,
            streetName: form.streetName,
            city: form.city,
            hiddenRemarks: form.hiddenRemarks,
            startDate: this.toDateOnlyString(form.startDate),
            vogDate: this.toDateOnlyString(form.vogDate),
            driversTestDate: this.toDateOnlyString(form.driversTestDate),
            addressCoordinates:
              form.addressCoordinates?._methodName === 'deleteField'
                ? null
                : form.addressCoordinates,
            addressCoordinatesFor:
              form.addressCoordinatesFor?._methodName === 'deleteField'
                ? null
                : form.addressCoordinatesFor,
            addressFingerprint:
              form.addressFingerprint?._methodName === 'deleteField'
                ? null
                : form.addressFingerprint,
            location: form.location?._methodName === 'deleteField' ? null : form.location,
          }
        : {}),
    };
    // const httpRequest = this.http.post(
    //   `${environment.functionsUrl}/httpGrantRights`,
    //   {
    //     email: form.email,
    //     password: this.newUser() ? form.password : null,
    //     name: ucFirst(form.name),
    //     roles: roles,
    //     organisationId: this.organisationId,
    //     phone: form.phone,
    //     hidden: true,
    //     withdrawRights: withdrawRights,
    //     passenger: form.passenger,
    //     userId: this.newUser() ? null : (this.user()?.id ?? null),
    //     isNewUser: this.newUser(),
    //   },
    //   { responseType: 'text' },
    // );
    await callable(params).then(
      async (result: any) => {
        const data = result.data;
        console.log('result', data);
        if (data === 'success') {
          try {
            await this.persistPhoto();
            openSnackbar(this.snackBar, 'De wijzigingen zijn opgeslagen.');
            this.dialogRef.close();
          } catch (error) {
            console.error(error);
            this.snackBar.open('De profielfoto kon niet worden opgeslagen.', 'X', {
              duration: 5000,
            });
          }
        } else if (data === 'email_already_exists') {
          this.snackBar.open('Dit e-mailadres is al in gebruik.', 'X', {
            duration: 5000,
          });
        } else if (data === 'error_creating_user') {
          this.userForm.controls.email.setErrors({ customError: true });
        }
        this.saving.set(false);
      },
      (error) => {
        console.log('error', error);
        this.saving.set(false);
      },
    );
    // httpRequest.subscribe(
    //   (result: string) => {
    //     console.log('result', result);
    //     if (result === 'success') {
    //       if (withdrawRights) {
    //         this.snackBar.open('De rechten van de medewerker zijn ingetrokken.', 'X', {
    //           duration: 5000,
    //         });
    //       } else {
    //         this.snackBar.open('De medewerker heeft rechten gekregen.', '', {
    //           duration: 5000,
    //         });
    //       }
    //       this.dialogRef.close();
    //     } else if (result === 'email_already_exists') {
    //       this.snackBar.open('Dit e-mailadres is al in gebruik.', 'X', {
    //         duration: 5000,
    //       });
    //     } else if (result === 'error_creating_user') {
    //       this.userForm.controls.email.setErrors({ customError: true });
    //     }
    //     this.saving.set(false);
    //   },
    //   (error) => {
    //     console.log('error', error);
    //     this.saving.set(false);
    //   },
    // );
  }

  private async persistPhoto(): Promise<void> {
    if (!this.selectedPhoto() && !this.photoRemoved()) return;

    let userId = this.user()?.id;
    if (!userId) {
      const userQuery = await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/users`),
          where('email', '==', this.userForm.controls.email.value),
        ),
      );
      userId = userQuery.docs[0]?.id;
    }
    if (!userId) {
      throw new Error('Unable to determine the employee document for the profile photo.');
    }

    await this.profileService.savePhoto(
      this.organisationId,
      userId,
      this.photoRemoved() ? null : this.selectedPhoto(),
      true,
    );
  }

  generatePassword() {
    this.userForm.controls.password.setValue(generateRandomString());
  }

  hasDateParseError(field: string): boolean {
    const control = this.userForm.controls[field];
    return control.hasError('matDatepickerParse') || control.hasError('dateInvalid');
  }

  private validDate: ValidatorFn = (control) => {
    if (!control.value) return null;
    return valueToDate(control.value) ? null : { dateInvalid: true };
  };

  private dateNotInFuture: ValidatorFn = (control) => {
    const dateError = this.validDate(control);
    if (dateError || !control.value) return dateError;
    return moment(valueToDate(control.value)).isAfter(moment(), 'day')
      ? { futureDate: true }
      : null;
  };

  private toDateOnlyString(value: any): string | null {
    const date = valueToDate(value);
    return date ? moment(date).format('YYYY-MM-DD') : null;
  }

  private setupAddressLookup() {
    const lookup = this.addressService.connect(this.userForm, this.destroyRef);
    this.isLookingUp = lookup.isLookingUp;
    this.addressNotFound = lookup.addressNotFound;
  }
}
